import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Pagelayout from "../components/Pagelayout";
import CustomTable from "../components/ListPageComponent";
import PaySlipCard from "../components/PaySlipCard";
import { useNavigate } from "react-router-dom";

import { fetchAllPayslipAPI } from "../parollAPIs";
import { useDispatch, useSelector } from "react-redux";
import {
  extractValues,
  formatDateTime,
  toUTC,
} from "../../../../util/utilityFunctions";
import { setStatusShowOption } from "../../../filter/filterSlice";
import { setPaginationData } from "../payrollSlice";
import { HEADERS } from "../../../../API/UrlAndPaths";

const Payslip = () => {
  //router-dom
  const navigate = useNavigate();
  const dispatch = useDispatch()

  //remove local singleViewId in local storage
  localStorage.removeItem("singleViewId");
  
  // redux
  const { paginationData, searchInput, filterData } = useSelector(
    (state) => state.payrollSlice
  );
  // local states
  const [data, setData] = useState([]);
  const [employeeImages, setEmployeeImages] = useState({});
  const [isGridView, setIsGridView] = useState(false);

  // Memoized image component to prevent re-renders (same as PayrollLeave)
  const EmployeeImage = useMemo(() => {
    // Inline SVG placeholder (40x40 circle) eliminates network 404 loops
    const FALLBACK_DATA_URL =
      'data:image/svg+xml;utf8,' +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
          <rect width="40" height="40" fill="#f0f0f0"/>
          <circle cx="20" cy="15" r="8" fill="#c0c0c0"/>
          <rect x="10" y="26" width="20" height="8" rx="4" fill="#d0d0d0"/>
        </svg>`
      );

    return ({ src, alt }) => (
      <img
        src={src || FALLBACK_DATA_URL}
        alt={alt}
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          objectFit: 'cover',
          border: '1px solid #e0e0e0'
        }}
        onError={(e) => {
          // Prevent infinite onError loop by removing handler, then set fallback
          e.currentTarget.onerror = null;
          e.currentTarget.src = FALLBACK_DATA_URL;
        }}
      />
    );
  }, []);

  // Fetch employee image by ID
  const fetchEmployeeImage = async (employeeId) => {
    if (!employeeId || employeeImages[employeeId]) return employeeImages[employeeId];
    
    try {
      console.log('Fetching employee image for ID:', employeeId);
      const response = await axios.post(`employee/getEmployeeById`, { id: employeeId }, HEADERS);
      console.log('API response:', response.data);
      const employee = response.data?.data || response.data;
      if (employee) {
        const imageUrl = 
          employee.imageUrl ||
          employee.profileImage ||
          employee.image ||
          employee.employeePhoto ||
          employee.empImage ||
          null;
        
        console.log('Found image URL:', imageUrl);
        if (imageUrl) {
          setEmployeeImages(prev => ({ ...prev, [employeeId]: imageUrl }));
          return imageUrl;
        }
      }
    } catch (error) {
      console.warn('Failed to fetch employee image for ID:', employeeId, error);
    }
    return null;
  };

  // list headers
  const headers = [
    { key: "employeeImage", label: "Image", width: 56 },
    { key: "empCode", label: "Employee" },
    { key: "fromDate", label: "Date from" },
    { key: "toDate", label: "Date to" },
    { key: "branch", label: "Branch" },
    { key: "department", label: "Department" },
    { key: "designation", label: "Designation" },
    { key: "status", label: "Status" },
  ];

  // Only show specific columns
  const visibleColumns = [
    "employeeImage",
    "empCode",
    "fromDate",
    "toDate",
    "branch",
    "department",
    "designation",
    "status",
  ];

  const handleRowClick = (rowData) => {
    // console.log("Clicked row data:", rowData);
    // console.log(rowData)
    // navigate with the row data
    navigate("create", { state: { _id: rowData?._id } });
    localStorage.setItem("singleViewId", rowData?._id);
  };
  const handleCreateBtn = () => {
    navigate("create");
  };

  //refetch
  useEffect(() => {
    dispatch(setStatusShowOption(["DRAFT", "POSTED","DONE"]))

    fetchAllPayslipAPI({
      body: {
        empId: extractValues(filterData?.employee || []),
        branchId: extractValues(filterData?.branch || []),
        designation: extractValues(filterData?.designation || []),
        department: extractValues(filterData?.department || []),
        status: extractValues(filterData?.status, "uppercase"),
        page: paginationData.currentPage,
        limit: 15,
        fromDate: toUTC(filterData?.date.from),
        toDate: toUTC(filterData?.date.to),
        search: searchInput || "",
      },
      successCallback: async (data) => {
        const formattedData = await Promise.all(data?.list?.map(async (item, idx) => {
          // Use employeeDetails._id to fetch the actual employee image
          const employeeId = item?.employeeDetails?._id;
          let imageUrl = null;
          
          if (employeeId) {
            imageUrl = await fetchEmployeeImage(employeeId);
          }
          
          // Build full URL if we have a relative path
          if (imageUrl && !imageUrl.startsWith('http')) {
            const base = (axios.defaults.baseURL || '').replace(/\\\\/g, '/').replace(/\/$/, '');
            const path = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
            imageUrl = `${base}${path}`;
          }
          
          if (idx === 0) {
            console.log(
              "Payslip - Employee ID:", employeeId,
              "Fetched image URL:", imageUrl,
              "Base URL:", axios.defaults.baseURL
            );
          }

          return {
            empCode: item?.empCode,
            designation: item?.designationData?.designation,
            department: item?.departmentData?.departmentName,
            branch: item?.branchDetails?.branchName,
            fromDate: formatDateTime(item?.fromDate),
            toDate: formatDateTime(item?.toDate),
            status: item?.status?.toLowerCase(),
            employeeImage: imageUrl,
            _id: item?._id,
          };
        }));
        setData(formattedData);
        dispatch(
          setPaginationData({
            currentPage: paginationData?.currentPage || 1,
            totalPages: data?.totalPages || 0,
          })
        );
      },
      errorCallback: (error) => {
        setData([]);
      },
    });
  }, [
    searchInput,
    paginationData?.currentPage,
    filterData?.employee,
    filterData?.branch,
    filterData?.date,
    filterData?.department,
    filterData?.designation,
    filterData?.status,
  ]);

  // initial fetching
  useEffect(() => {
    dispatch(setStatusShowOption(["DRAFT", "POSTED","DONE"]))
    // Fetch data from the API
    fetchAllPayslipAPI({
      body: {
        empId: extractValues(filterData?.employee || []),
        branchId: extractValues(filterData?.branch || []),
        designation: extractValues(filterData?.designation || []),
        department: extractValues(filterData?.department || []),
        status: extractValues(filterData?.status, "uppercase"),
        page: paginationData.currentPage,
        limit: 15,
        fromDate: toUTC(filterData?.date.from),
        toDate: toUTC(filterData?.date.to),
        search: searchInput || "",
      },
      successCallback: async (data) => {
        const formattedData = await Promise.all(data?.list?.map(async (item, idx) => {
          // Use employeeDetails._id to fetch the actual employee image
          const employeeId = item?.employeeDetails?._id;
          let imageUrl = null;
          
          if (employeeId) {
            imageUrl = await fetchEmployeeImage(employeeId);
          }
          
          // Build full URL if we have a relative path
          if (imageUrl && !imageUrl.startsWith('http')) {
            const base = (axios.defaults.baseURL || '').replace(/\\\\/g, '/').replace(/\/$/, '');
            const path = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
            imageUrl = `${base}${path}`;
          }
          
          if (idx === 0) {
            console.log(
              "Payslip - Employee ID:", employeeId,
              "Fetched image URL:", imageUrl,
              "Base URL:", axios.defaults.baseURL
            );
          }

          return {
            empCode: item?.empCode,
            designation: item?.designationData?.designation,
            department: item?.departmentData?.departmentName,
            branch: item?.branchDetails?.branchName,
            fromDate: formatDateTime(item?.fromDate),
            toDate: formatDateTime(item?.toDate),
            status: item?.status?.toLowerCase(),
            employeeImage: imageUrl,
            _id: item?._id,
          };
        }));
        setData(formattedData);
      },
      errorCallback: (error) => {
        setData([]);
      },
    });
  }, []);

  return (
    <Pagelayout
      pagination={paginationData.totalPages > 1}
      headerLayout={1}
      pageName={"Pay Slip"}
      handleCreateBtn={handleCreateBtn}
      isGridView={isGridView}
      setIsGridView={setIsGridView}
      gridView={true}
      filterOptions={[
        "date",
        "branch",
        "status",
        "employee",
        "designation",
        "department",
      ]}

    >
      {!isGridView ? (
        <CustomTable
          headers={headers}
          data={data}
          visibleColumns={visibleColumns}
          onRowClick={handleRowClick}
          loading={false}
        />
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1em" }}>
          {data?.map((r, i) => (
            <PaySlipCard data={r} onCardClick={handleRowClick} />
          ))}
        </div>
      )}
    </Pagelayout>
  );
};

export default Payslip;
